import { useEffect } from 'react';
import './NeedleArmAnimation.css';

/**
 * PROFESSIONAL NEEDLE ARM ANIMATION
 * Grass-type physical move with arm transformation and piercing strike
 * 
 * Key Features:
 * - Grass-type physical attack (60 base power, 100% accuracy)
 * - Arm becomes needle-like for piercing strike
 * - Transformation effects with sharp needle mechanics
 * - Professional gaming company quality with botanical timing
 * - Physical move animation with transformation and piercing mechanics
 */
export default function NeedleArmAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate strike trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Needle Transformation (0-600ms)
    console.log('Needle Arm Phase 1: Needle Transformation');
    
    // Player enhancement with transformation aura
    playerEl.classList.add('needle-arm-user-charge');
    
    // Transformation energy aura around player
    const transformAura = document.createElement('div');
    transformAura.className = 'needle-arm-transform-aura';
    transformAura.style.left = `${playerX - 85}px`;
    transformAura.style.top = `${playerY - 85}px`;
    container.appendChild(transformAura);
    elements.push(transformAura);

    // Needle energy particles around player
    for (let i = 0; i < 14; i++) {
      const needleParticle = document.createElement('div');
      needleParticle.className = 'needle-arm-needle-particle';
      const angle = (i / 14) * 360;
      const radius = 55 + (i % 4) * 8;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      needleParticle.style.left = `${particleX - 3}px`;
      needleParticle.style.top = `${particleY - 3}px`;
      needleParticle.style.animationDelay = `${i * 30}ms`;
      needleParticle.style.transform = `rotate(${angle}deg)`;
      needleParticle.textContent = '🪡';
      container.appendChild(needleParticle);
      elements.push(needleParticle);
    }

    // Thorn growth effects showing transformation
    for (let i = 0; i < 10; i++) {
      const thorn = document.createElement('div');
      thorn.className = 'needle-arm-thorn-growth';
      const thornAngle = (i / 10) * 360;
      const thornRadius = 70;
      const thornX = playerX + Math.cos(thornAngle * Math.PI / 180) * thornRadius;
      const thornY = playerY + Math.sin(thornAngle * Math.PI / 180) * thornRadius;
      thorn.style.left = `${thornX - 4}px`;
      thorn.style.top = `${thornY - 4}px`;
      thorn.style.animationDelay = `${i * 45}ms`;
      thorn.textContent = '🌿';
      container.appendChild(thorn);
      elements.push(thorn);
    }

    // Arm transformation visual effect
    const armTransform = document.createElement('div');
    armTransform.className = 'needle-arm-transformation';
    armTransform.style.left = `${playerX + 20}px`;
    armTransform.style.top = `${playerY - 10}px`;
    armTransform.textContent = '💪➡️🪡';
    container.appendChild(armTransform);
    elements.push(armTransform);

    // PHASE 2: Needle Strike Dash (600-900ms)
    const phase2Timer = setTimeout(() => {
      console.log('Needle Arm Phase 2: Needle Strike Dash');
      
      // Player dash movement toward opponent
      playerEl.classList.add('needle-arm-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Needle strike trail during dash
      for (let i = 0; i < 8; i++) {
        const trailTimer = setTimeout(() => {
          const needleTrail = document.createElement('div');
          needleTrail.className = 'needle-arm-strike-trail';
          const progress = i / 8;
          const trailX = playerX + (dx * 0.85 * progress) + (Math.random() - 0.5) * 15;
          const trailY = playerY + (dy * 0.85 * progress) + (Math.random() - 0.5) * 15;
          needleTrail.style.left = `${trailX - 2}px`;
          needleTrail.style.top = `${trailY - 2}px`;
          needleTrail.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
          needleTrail.textContent = '🪡';
          container.appendChild(needleTrail);
          elements.push(needleTrail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Sharp wind effects during dash
      for (let i = 0; i < 6; i++) {
        const windTimer = setTimeout(() => {
          const sharpWind = document.createElement('div');
          sharpWind.className = 'needle-arm-sharp-wind';
          const windX = playerX + (dx * 0.85 * (i / 6)) + (Math.random() - 0.5) * 30;
          const windY = playerY + (dy * 0.85 * (i / 6)) + (Math.random() - 0.5) * 20;
          sharpWind.style.left = `${windX - 4}px`;
          sharpWind.style.top = `${windY - 4}px`;
          sharpWind.textContent = '💨';
          container.appendChild(sharpWind);
          elements.push(sharpWind);
        }, i * 35);
        elements.push({ timer: windTimer, type: 'timer' });
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Needle Piercing Impact (900-1300ms)
    const phase3Timer = setTimeout(() => {
      console.log('Needle Arm Phase 3: Needle Piercing Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('needle-arm-opponent-hit');

      // Reset player position with strike effect
      playerEl.classList.remove('needle-arm-user-dash');
      playerEl.classList.add('needle-arm-user-strike');

      // Piercing impact effect
      const piercingImpact = document.createElement('div');
      piercingImpact.className = 'needle-arm-piercing-impact';
      piercingImpact.style.left = `${opponentX - 100}px`;
      piercingImpact.style.top = `${opponentY - 100}px`;
      container.appendChild(piercingImpact);
      elements.push(piercingImpact);

      // Needle penetration effects
      for (let i = 0; i < 12; i++) {
        const penetration = document.createElement('div');
        penetration.className = 'needle-arm-penetration';
        const penAngle = (i / 12) * 360;
        const penRadius = 60 + Math.random() * 40;
        const penX = opponentX + Math.cos(penAngle * Math.PI / 180) * penRadius;
        const penY = opponentY + Math.sin(penAngle * Math.PI / 180) * penRadius;
        penetration.style.left = `${penX - 3}px`;
        penetration.style.top = `${penY - 3}px`;
        penetration.style.animationDelay = `${i * 20}ms`;
        penetration.style.transform = `rotate(${penAngle}deg)`;
        penetration.textContent = '🪡';
        container.appendChild(penetration);
        elements.push(penetration);
      }

      // Sharp impact shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'needle-arm-shockwave';
          shockwave.style.left = `${opponentX - 75}px`;
          shockwave.style.top = `${opponentY - 75}px`;
          shockwave.style.animationDelay = `${i * 90}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 110);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Thorn burst effects
      for (let i = 0; i < 16; i++) {
        const thornBurst = document.createElement('div');
        thornBurst.className = 'needle-arm-thorn-burst';
        const burstAngle = (i / 16) * 360;
        const burstRadius = 80;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        thornBurst.style.left = `${burstX - 4}px`;
        thornBurst.style.top = `${burstY - 4}px`;
        thornBurst.style.animationDelay = `${i * 18}ms`;
        thornBurst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * 30}px`);
        thornBurst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * 30}px`);
        thornBurst.textContent = '🌿';
        container.appendChild(thornBurst);
        elements.push(thornBurst);
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Arm Recovery (1300-1600ms)
    const phase4Timer = setTimeout(() => {
      console.log('Needle Arm Phase 4: Arm Recovery');
      
      // Remove transformation effects
      playerEl.classList.remove('needle-arm-user-charge', 'needle-arm-user-strike');
      
      // Arm restoration effect
      const armRestore = document.createElement('div');
      armRestore.className = 'needle-arm-restoration';
      armRestore.style.left = `${playerX + 15}px`;
      armRestore.style.top = `${playerY - 15}px`;
      armRestore.textContent = '🪡➡️💪';
      container.appendChild(armRestore);
      elements.push(armRestore);

      // Healing leaf effects
      for (let i = 0; i < 8; i++) {
        const healingLeaf = document.createElement('div');
        healingLeaf.className = 'needle-arm-healing-leaf';
        const leafX = playerX + (Math.random() - 0.5) * 80;
        const leafY = playerY + (Math.random() - 0.5) * 60;
        healingLeaf.style.left = `${leafX - 4}px`;
        healingLeaf.style.top = `${leafY - 4}px`;
        healingLeaf.style.animationDelay = `${i * 35}ms`;
        healingLeaf.textContent = '🍃';
        container.appendChild(healingLeaf);
        elements.push(healingLeaf);
      }

      // Gentle wind recovery
      const recoveryWind = document.createElement('div');
      recoveryWind.className = 'needle-arm-recovery-wind';
      recoveryWind.style.left = `${playerX - 60}px`;
      recoveryWind.style.top = `${playerY - 60}px`;
      container.appendChild(recoveryWind);
      elements.push(recoveryWind);
    }, 1300);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1600ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Needle Arm: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('needle-arm-user-charge', 'needle-arm-user-dash', 'needle-arm-user-strike');
      opponentEl.classList.remove('needle-arm-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1600);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('needle-arm-user-charge', 'needle-arm-user-dash', 'needle-arm-user-strike');
      opponentEl.classList.remove('needle-arm-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
NeedleArmAnimation.moveType = 'grass';
